/**
 * The theme from TypeScript. Colours live once, as CSS variables in styles.css; DOM code uses them via
 * Tailwind classes (`text-gilt`), and canvas code reads them here so a theme switch repaints both.
 * See docs/STYLE.md.
 */

export const COLOR_TOKENS = [
  'ink', 'ink-dim', 'ink-faint', 'vellum', 'vellum-raised', 'rule',
  'gilt', 'gilt-bright', 'blood', 'verdigris', 'woad', 'ochre', 'heather', 'ember',
  'scene-grass', 'scene-wood',
] as const;
export type ColorToken = (typeof COLOR_TOKENS)[number];
export type Palette = Record<ColorToken, string>;

let cache: { key: string | undefined; palette: Palette } | null = null;

/** Every colour token as a resolved CSS colour. Cheap to call per frame: re-read only when `data-theme` changes. */
export function palette(): Palette {
  const root = document.documentElement;
  const key = root.dataset.theme;
  if (cache && cache.key === key) return cache.palette;
  const css = getComputedStyle(root);
  const read = (t: ColorToken) => css.getPropertyValue(`--color-${t}`).trim();
  const fresh = Object.fromEntries(COLOR_TOKENS.map((t) => [t, read(t)])) as Palette;
  cache = { key, palette: fresh };
  return fresh;
}

/** A token at some opacity, for canvas strokes and washes (the `bg-gilt/30` of canvas). */
export function withAlpha(color: string, a: number) {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
  if (!m) return `color-mix(in srgb, ${color} ${Math.round(a * 100)}%, transparent)`;
  return `rgba(${parseInt(m[1]!, 16)},${parseInt(m[2]!, 16)},${parseInt(m[3]!, 16)},${a})`;
}

/**
 * Semantic tones, for anything that says how something went: outcome callouts, fx banners, results.
 * Classes are written out whole so Tailwind finds them.
 */
export type Tone = 'good' | 'gold' | 'bad' | 'warn' | 'muted' | 'blue';
export const TONE_TEXT: Record<Tone, string> = {
  good: 'text-verdigris',
  gold: 'text-gilt',
  bad: 'text-blood',
  warn: 'text-ochre',
  muted: 'text-ink-dim',
  blue: 'text-woad',
};
export const TONE_BORDER: Record<Tone, string> = {
  good: 'border-verdigris',
  gold: 'border-gilt/60',
  bad: 'border-blood',
  warn: 'border-ochre',
  muted: 'border-ink-faint',
  blue: 'border-woad',
};
